<template>
  <div class="flex h-screen bg-gray-50 overflow-hidden font-sans">
    <transition
      enter-active-class="transition-opacity duration-300 ease-linear"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-300 ease-linear"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isSidebarOpen"
        @click="isSidebarOpen = false"
        class="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
      ></div>
    </transition>

    <aside
      class="w-64 bg-slate-900 text-slate-300 flex flex-col shrink-0 fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out lg:static lg:translate-x-0"
      :class="isSidebarOpen ? 'translate-x-0' : '-translate-x-full'"
    >
      <div
        class="h-16 flex items-center px-6 text-white font-bold text-xl border-b border-slate-800"
      >
        <span class="bg-slate-700 p-1.5 rounded-lg mr-2 text-slate-300">
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        </span>
        Settings
      </div>

      <nav class="flex-1 px-3 py-4 space-y-6 overflow-y-auto">
        <div>
          <RouterLink
            to="/"
            class="flex items-center gap-3 px-3 py-2 text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Dashboard
          </RouterLink>
        </div>

        <div>
          <h3 class="px-3 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Users</h3>
          <div class="space-y-1">
            <RouterLink
              :to="{ name: 'MembershipList' }"
              class="block px-3 py-2 text-sm rounded-lg transition-colors"
              inactive-class="text-slate-400 hover:text-white hover:bg-slate-800/50"
              :class="{ 'bg-blue-600/10 text-blue-400 font-semibold': isMembershipRouteActive }"
            >
              Memberships
            </RouterLink>
            <RouterLink
              :to="{ name: 'CommissionPlanList' }"
              class="block px-3 py-2 text-sm rounded-lg transition-colors"
              active-class="bg-blue-600/10 text-blue-400 font-semibold"
              inactive-class="text-slate-400 hover:text-white hover:bg-slate-800/50"
            >
              Commission Plans
            </RouterLink>
            <RouterLink
              :to="{ name: 'NotificationProfileList' }"
              class="block px-3 py-2 text-sm rounded-lg transition-colors"
              active-class="bg-blue-600/10 text-blue-400 font-semibold"
              inactive-class="text-slate-400 hover:text-white hover:bg-slate-800/50"
            >
              Notification Profiles
            </RouterLink>
          </div>
        </div>
        <div>
          <h3 class="px-3 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
            Business
          </h3>
          <div class="space-y-1">
            <RouterLink
              :to="{ name: 'LocationList' }"
              class="block px-3 py-2 text-sm rounded-lg transition-colors"
              active-class="bg-blue-600/10 text-blue-400 font-semibold"
              inactive-class="text-slate-400 hover:text-white hover:bg-slate-800/50"
            >
              Locations
            </RouterLink>
            <RouterLink
              :to="{ name: 'EditPricing' }"
              class="block px-3 py-2 text-sm rounded-lg transition-colors"
              active-class="bg-blue-600/10 text-blue-400 font-semibold"
              inactive-class="text-slate-400 hover:text-white hover:bg-slate-800/50"
            >
              Pricing Rules
            </RouterLink>
            <RouterLink
              v-if="FEATURE_FLAGS.jobs"
              :to="{ name: 'JobFormList' }"
              class="block px-3 py-2 text-sm rounded-lg transition-colors"
              active-class="bg-blue-600/10 text-blue-400 font-semibold"
              inactive-class="text-slate-400 hover:text-white hover:bg-slate-800/50"
            >
              Job Forms
            </RouterLink>
            <RouterLink
              v-if="FEATURE_FLAGS.notifications"
              :to="{ name: 'EmailTemplateList' }"
              class="block px-3 py-2 text-sm rounded-lg transition-colors"
              active-class="bg-blue-600/10 text-blue-400 font-semibold"
              inactive-class="text-slate-400 hover:text-white hover:bg-slate-800/50"
            >
              Email Templates
            </RouterLink>
            <RouterLink
              :to="{ name: 'ApiKeys' }"
              class="block px-3 py-2 text-sm rounded-lg transition-colors"
              inactive-class="text-slate-400 hover:text-white hover:bg-slate-800/50"
              :class="{ 'bg-blue-600/10 text-blue-400 font-semibold': isApiKeysRouteActive }"
            >
              API Keys
            </RouterLink>
            <RouterLink
              :to="{ name: 'EditNewLeadsPipeline' }"
              class="block px-3 py-2 text-sm rounded-lg transition-colors"
              inactive-class="text-slate-400 hover:text-white hover:bg-slate-800/50"
              :class="{ 'bg-blue-600/10 text-blue-400 font-semibold': isPipelineRouteActive }"
            >
              Pipelines
            </RouterLink>
            <RouterLink
              :to="{ name: 'EditAppointmentSettings' }"
              class="block px-3 py-2 text-sm rounded-lg transition-colors"
              inactive-class="text-slate-400 hover:text-white hover:bg-slate-800/50"
              :class="{
                'bg-blue-600/10 text-blue-400 font-semibold': isBusinessSettingsRouteActive,
              }"
            >
              Settings
            </RouterLink>
          </div>
        </div>

        <div>
          <h3 class="px-3 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
            Catalog
          </h3>
          <div class="space-y-1">
            <RouterLink
              :to="{ name: 'ServiceList' }"
              class="block px-3 py-2 text-sm rounded-lg transition-colors"
              inactive-class="text-slate-400 hover:text-white hover:bg-slate-800/50"
              :class="{ 'bg-blue-600/10 text-blue-400 font-semibold': isServiceRouteActive }"
            >
              Services
            </RouterLink>
            <RouterLink
              :to="{ name: 'ProductList' }"
              class="block px-3 py-2 text-sm rounded-lg transition-colors"
              inactive-class="text-slate-400 hover:text-white hover:bg-slate-800/50"
              :class="{ 'bg-blue-600/10 text-blue-400 font-semibold': isProductRouteActive }"
            >
              Products
            </RouterLink>
          </div>
        </div>
      </nav>
    </aside>

    <div class="flex-1 flex flex-col overflow-hidden min-w-0">
      <header
        class="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-8 shrink-0 shadow-sm z-10"
      >
        <div class="flex items-center gap-3 lg:gap-4">
          <button
            @click="isSidebarOpen = true"
            class="p-2 -ml-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 rounded-lg lg:hidden transition-colors cursor-pointer"
          >
            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>

          <span
            class="text-xs font-bold bg-slate-100 text-slate-500 px-2 py-1 rounded tracking-widest uppercase hidden sm:inline-block"
          >
            Settings
          </span>
          <h2
            class="text-sm font-bold text-slate-800 uppercase tracking-wider sm:border-l sm:pl-4 border-gray-200"
          >
            {{ currentRouteName }}
          </h2>
        </div>
      </header>

      <main class="flex-1 overflow-y-auto bg-slate-50 relative">
        <RouterView />
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { FEATURE_FLAGS } from '@/config/featureFlags'

const route = useRoute()
const isSidebarOpen = ref(false)

watch(
  () => route.path,
  () => {
    isSidebarOpen.value = false
  },
)

onMounted(() => {
  isSidebarOpen.value = true
})

const isProductRouteActive = computed(() => {
  return route.path.startsWith('/settings/catalog')
})

const isMembershipRouteActive = computed(() => {
  return route.path.startsWith('/settings/user')
})

const isApiKeysRouteActive = computed(() => {
  return route.path.startsWith('/settings/api-keys')
})

const isPipelineRouteActive = computed(() => {
  return route.path.startsWith('/settings/pipelines')
})

const isBusinessSettingsRouteActive = computed(() => {
  return route.path.startsWith('/settings/business')
})

const isServiceRouteActive = computed(() => {
  return route.path.startsWith('/settings/services')
})

const currentRouteName = computed(() => {
  return route.name?.replace(/([A-Z])/g, ' $1').trim() || 'Configuration'
})
</script>
